import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../store'
import { setRoomMatches, setLoading } from '../store/matchesSlice'
import { setSingleRoom } from '../store/roomsSlice'
import matchService from '../services/matchService'
import roomsService from '../services/roomsService'
import { User } from '../types/room'
import MatchCard from '../components/MatchCard'
import { useTranslation } from '../hooks/use-i18n'
import { FOOTBALL_LIVE_POLL_MS } from '../utils/footballLiveScore'

const Rankings: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { roomMatches } = useSelector((state: RootState) => state.matches)
  const { singleRoom } = useSelector((state: RootState) => state.rooms)

  const roomQuery = useQuery({
    queryKey: ['room-summary', roomId],
    queryFn: () => roomsService.getRoomSummary(roomId!),
    enabled: Boolean(roomId),
    staleTime: 30 * 1000,
    refetchInterval: FOOTBALL_LIVE_POLL_MS,
  })

  const matchesQuery = useQuery({
    queryKey: ['room-matches', roomId],
    queryFn: () => matchService.getRoomMatches(roomId!),
    enabled: Boolean(roomId),
    staleTime: 30 * 1000,
    refetchInterval: FOOTBALL_LIVE_POLL_MS,
  })

  const room = roomQuery.data ?? singleRoom
  const [activeTab, setActiveTab] = useState<'rankings' | 'matches'>('rankings')

  useEffect(() => {
    if (roomQuery.data) dispatch(setSingleRoom(roomQuery.data))
    if (roomQuery.error) dispatch(setSingleRoom(null))
  }, [roomQuery.data, roomQuery.error, dispatch])

  useEffect(() => {
    if (matchesQuery.data !== undefined) dispatch(setRoomMatches({ roomId: roomId!, matches: matchesQuery.data }))
  }, [matchesQuery.data, roomId, dispatch])

  useEffect(() => {
    dispatch(setLoading(roomQuery.isLoading && !roomQuery.data))
  }, [roomQuery.isLoading, roomQuery.data, dispatch])

  const computeUserPoints = useCallback((player: User): number => {
    if (typeof (player as any).totalPoints === 'number') {
      return (player as any).totalPoints
    }
    if (!player.predictions || !Array.isArray(player.predictions)) return 0
    return player.predictions.reduce((acc: number, prediction) => {
      const scorePoints = Number(prediction.predictedScorePoint) || 0
      const winnerPoints = Number(prediction.predictedWinnerPoint) || 0
      const etPoints = Number((prediction as any).predictedWinnerAdditionalTimePoint) || 0
      const penPoints = Number((prediction as any).predictedWinnerPenaltiesPoint) || 0
      return acc + scorePoints + winnerPoints + etPoints + penPoints
    }, 0)
  }, [])

  // Sort players by points (same as React Native app)
  const sortedPlayers = useMemo(() => {
    if (!room) return []
    return [...room.players].sort((a, b) => computeUserPoints(b) - computeUserPoints(a))
  }, [room, computeUserPoints])

  const isLoading = roomQuery.isLoading && !roomQuery.data

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    )
  }

  if (!room) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{t('rankings.roomNotFound')}</h2>
          <Link to="/dashboard" className="text-sky-400 hover:text-sky-300">
            {t('rankings.backToDashboard')}
          </Link>
        </div>
      </div>
    )
  }

  const roomTypeLabel = room.type === 'company' ? t('room.typeCompany') : t('room.typeIndividual')

  const matches = roomMatches[roomId || ''] || []

  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-0.5 sm:px-6 lg:px-8 py-3 sm:py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-8 space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <Link 
              to="/dashboard" 
              className="text-white/80 hover:text-white transition-colors text-sm sm:text-base"
            >
              {t('rankings.back')}
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="text-xs sm:text-xl lg:text-2xl font-bold text-white bg-gradient-to-r from-sky-400 to-blue-600 bg-clip-text text-transparent leading-tight max-w-48 truncate">
                {room.name}
              </h1>
              <p className="text-white/80 text-xs sm:text-sm">
                {roomTypeLabel} • {room.players.length} {t('rankings.players')}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end">
            <span className="text-white/80 text-sm sm:text-sm">
              {t('rankings.owner')}: {room.owner.name}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-white/10 rounded-xl p-1 mb-3 sm:mb-8">
          <button
            onClick={() => setActiveTab('rankings')}
            className={`flex-1 py-2 sm:py-3 px-3 sm:px-6 rounded-lg font-semibold transition-all duration-300 text-sm sm:text-base ${
              activeTab === 'rankings'
                ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg'
                : 'text-white/80 hover:text-white hover:bg-white/10'
            }`}
          >
            {t('rankings.rankingsTab')} ({sortedPlayers.length})
          </button>
          <button
            onClick={() => setActiveTab('matches')}
            className={`flex-1 py-2 sm:py-3 px-3 sm:px-6 rounded-lg font-semibold transition-all duration-300 text-sm sm:text-base ${
              activeTab === 'matches'
                ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg'
                : 'text-white/80 hover:text-white hover:bg-white/10'
            }`}
          >
            {t('rankings.matchesTab')} ({matches.length})
          </button>
        </div>

        {/* Content */}
        {activeTab === 'rankings' ? (
          <div className="py-2 sm:py-8">
            {sortedPlayers.length === 0 ? (
              <div className="text-center py-8 sm:py-16 blue-sky-card rounded-2xl p-6 sm:p-8 max-w-md mx-auto">
                <div className="text-6xl sm:text-8xl mb-4 sm:mb-6 animate-pulse">📊</div>
                <h3 className="text-lg sm:text-2xl font-semibold mb-3 sm:mb-4 text-white">No rankings yet</h3>
                <p className="text-white/80 leading-relaxed text-sm sm:text-base">
                  Start making predictions to see the leaderboard come alive!
                </p>
              </div>
            ) : (
              <div className="space-y-1 sm:space-y-4">
                {sortedPlayers.map((player, index) => (
                  <div
                    key={player._id}
                    className={`blue-sky-card rounded-md sm:rounded-2xl p-1 sm:p-5 transition-all duration-300 ${
                      index < 3 ? 'border border-sky-400 shadow-lg' : ''
                    }`}
                  >
                    {/* Player Info Row */}
                    <div className="flex items-center space-x-1 mb-1">
                      <span className="text-sm sm:text-2xl font-bold text-white flex-shrink-0">
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                      </span>
                      <div className="w-5 h-5 sm:w-12 sm:h-12 bg-gradient-to-r from-red-500 to-white rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-xs sm:text-lg">
                          {player.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-xs sm:text-lg truncate">
                          {player.name}
                        </p>
                        <p className="text-white/70 text-xs">
                          {computeUserPoints(player)} • {player.predictions.length}
                        </p>
                      </div>
                    </div>
                    
                    {/* Buttons Row */}
                    <div className="flex justify-center space-x-1">
                      <button className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium transition-colors flex items-center space-x-0.5 flex-1 max-w-16">
                        <span className="text-xs">🎮</span>
                        <span className="text-xs">Game</span>
                      </button>
                      <button className="bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors flex items-center space-x-0.5 flex-1 max-w-16">
                        <span className="text-xs">📊</span>
                        <span className="text-xs">Stat</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {matches.length === 0 ? (
              <div className="text-center py-8 sm:py-16">
                <div className="blue-sky-card rounded-2xl sm:rounded-3xl p-8 sm:p-12 max-w-md mx-auto">
                  <div className="text-6xl sm:text-8xl mb-4 sm:mb-6">⚽</div>
                  <h3 className="text-lg sm:text-2xl font-semibold mb-3 sm:mb-4 text-white">No matches yet</h3>
                  <p className="text-white/80 mb-6 sm:mb-8 text-sm sm:text-base">
                    Matches will appear here when they are scheduled.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid gap-4 sm:gap-6">
                {matches.map((match) => (
                  <MatchCard key={match._id} match={match} roomId={roomId!} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Rankings
